// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useState } from 'react';

import type { HoleElevationProfile } from './elevation';

/**
 * Profiles are cached for the life of the app process.
 *
 * Terrain does not change, and re-analysing a course is a deliberate admin action that
 * already means reopening the app's data — so unlike the wind there is nothing to expire
 * against, and walking back to a hole should cost no request at all.
 */
const cache = new Map<string, HoleElevationProfile | null>();

/** Test seam: clears the module-level cache between cases. */
export const __clearElevationCache = () => cache.clear();

const cacheKey = (courseId: string, holeNumber: number) => `${courseId}/${holeNumber}`;

const num = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const parseProfile = (body: unknown, holeNumber: number): HoleElevationProfile | null => {
  if (!body || typeof body !== 'object') return null;
  const raw = (body as { points?: unknown }).points;
  if (!Array.isArray(raw)) return null;

  const points = raw.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return [];
    const record = entry as Record<string, unknown>;
    const lat = num(record.lat);
    const lon = num(record.lon);
    const elevationM = num(record.elevationM ?? record.ElevationM);
    return lat === null || lon === null || elevationM === null
      ? []
      : [{ lat, lon, elevationM }];
  });

  // One station cannot describe a slope, and zero cannot describe anything.
  return points.length < 2 ? null : { holeNumber, points };
};

export type HoleElevationState = {
  /** Null until loaded, and on any course the height model does not cover. */
  profile: HoleElevationProfile | null;
  loading: boolean;
};

export type UseHoleElevationParams = {
  apiBaseUrl: string;
  token: string | null | undefined;
  courseId: string | null;
  holeNumber: number;
  /** Nothing is fetched until the view is actually opened. */
  enabled: boolean;
};

/**
 * Terrain heights along one hole, for the height-difference readout.
 *
 * Failures are silent, the same call `useHoleWind` makes for the same reason: most courses
 * have no stored profile at all — the height model is Swedish, and a course has to have been
 * analysed since elevation was added — so "no reading" is the ordinary state rather than an
 * error, and there is nothing a player could do about it anyway. The badge simply does not
 * appear.
 */
export const useHoleElevation = ({
  apiBaseUrl,
  token,
  courseId,
  holeNumber,
  enabled,
}: UseHoleElevationParams): HoleElevationState => {
  const key = enabled && courseId && token ? cacheKey(courseId, holeNumber) : null;
  const [state, setState] = useState<HoleElevationState>({ profile: null, loading: false });

  useEffect(() => {
    if (!key || !courseId || !token) {
      setState({ profile: null, loading: false });
      return undefined;
    }

    if (cache.has(key)) {
      setState({ profile: cache.get(key) ?? null, loading: false });
      return undefined;
    }

    let cancelled = false;
    setState({ profile: null, loading: true });

    const load = async () => {
      try {
        const response = await fetch(
          `${apiBaseUrl}/api/geodata/courses/${courseId}/holes/${holeNumber}/elevation`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        if (!response.ok) {
          // A failed request is not a course without a profile: leave the cache empty so
          // reopening the hole tries again rather than remembering a network blip forever.
          setState({ profile: null, loading: false });
          return;
        }

        const profile = parseProfile(await response.json(), holeNumber);
        if (cancelled) return;
        cache.set(key, profile);
        setState({ profile, loading: false });
      } catch {
        if (!cancelled) setState({ profile: null, loading: false });
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, courseId, holeNumber, key, token]);

  return state;
};
